import { Checkbox, Input, Modal, Select, Switch, Tabs, Tag } from 'antd'
import type { HookAPI } from 'antd/es/modal/useModal'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import Loader from '@/components/Atoms/Loader/Loader'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import {
  PartnershipContextResponse,
  PartnershipModel,
  PartnershipSupplierModel,
  StoreShortModel,
  SupplierDirectoryItemDto,
  SupplierDirectoryResponse,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { sortPartnershipContext } from '@/utils/search/stores/sortStores'

import cn from './InviteModal.module.scss'

const inviteFormSchema = z.object({
  myB2bSegmentId: z
    .string({ required_error: 'Выберите B2B сегмент' })
    .min(1, 'Выберите B2B сегмент'),
})

interface Props {
  open: boolean
  preselectedSupplier?: PartnershipSupplierModel | null
  titleOverride?: string
  onClose: () => void
  onSuccess: (partnership: PartnershipModel) => void
  modal: HookAPI
}

interface FormState {
  targetSupplierId: string
  enableMySharing: boolean
  shareAllOutgoing: boolean
  localOutgoingExclusions: number[]
  acceptAllIncoming: boolean
  localIncomingExclusions: number[]
  myEtaDays: number
  myIncomingEtaDays: number
  mySharedSegments: string[]
  myB2bSegmentId: string | null
}

const EMPTY_FORM: FormState = {
  targetSupplierId: '',
  enableMySharing: true,
  shareAllOutgoing: true,
  localOutgoingExclusions: [],
  acceptAllIncoming: true,
  localIncomingExclusions: [],
  myEtaDays: 0,
  myIncomingEtaDays: 0,
  mySharedSegments: [],
  myB2bSegmentId: null,
}

function StoreLabel({ store }: { store: StoreShortModel }) {
  return (
    <span className={cn.storeLabel}>
      <span>{store.description}</span>
      <span
        className={`${cn.storeStatusDot} ${store.isActive ? cn.activeStoreStatus : cn.inactiveStoreStatus}`}
        title={store.isActive ? 'Активен' : 'Неактивен'}
        aria-label={store.isActive ? 'Активен' : 'Неактивен'}
      />
      {store.isRemote && <Tag color="default">Удалённый</Tag>}
    </span>
  )
}

function InviteModal({
  open,
  preselectedSupplier,
  titleOverride,
  onClose,
  onSuccess,
  modal,
}: Props) {
  const [directoryItems, setDirectoryItems] = useState<
    SupplierDirectoryItemDto[]
  >([])
  const [directoryLoading, setDirectoryLoading] = useState(false)
  const [contextData, setContextData] =
    useState<PartnershipContextResponse | null>(null)
  const [contextLoading, setContextLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [b2bSegmentError, setB2bSegmentError] = useState(false)
  const [supplierError, setSupplierError] = useState(false)
  const [activeTab, setActiveTab] = useState('sell')
  const { selectedOrganisation } = useUserInfoStore((state) => state)
  const isOrgSelected = Object.keys(selectedOrganisation).length > 0
  const themeClass = isOrgSelected ? cn.blueTheme : cn.orangeTheme

  useEffect(() => {
    if (!open) return

    setB2bSegmentError(false)
    setSupplierError(false)
    setActiveTab('sell')
    setContextData(null)

    const supplierId = preselectedSupplier?.id ?? ''
    setForm({ ...EMPTY_FORM, targetSupplierId: supplierId })

    if (!preselectedSupplier) {
      setDirectoryLoading(true)
      api
        .get<SupplierDirectoryResponse>('/partnerships/directory')
        .then((res) => {
          const invitable = (res.data.items || []).filter((item) => {
            const p = item.partnershipShortDto
            if (!p) return true
            return p.status === 'TERMINATED' || p.status === 'REJECTED'
          })
          setDirectoryItems(invitable)
        })
        .catch((e) => handleApiError(e, modal))
        .finally(() => setDirectoryLoading(false))
    } else {
      loadContext(supplierId)
    }
  }, [open])

  const loadContext = (supplierId: string) => {
    if (!supplierId) return
    setContextLoading(true)
    api
      .get<PartnershipContextResponse>('/partnerships/context/settings', {
        params: { targetSupplierId: supplierId },
      })
      .then((res) => setContextData(sortPartnershipContext(res.data)))
      .catch((e) => handleApiError(e, modal))
      .finally(() => setContextLoading(false))
  }

  const handleSupplierSelect = (supplierId: string) => {
    setSupplierError(false)
    setContextData(null)
    setForm((prev) => ({
      ...prev,
      targetSupplierId: supplierId,
      localOutgoingExclusions: [],
      localIncomingExclusions: [],
      mySharedSegments: [],
      myB2bSegmentId: null,
    }))
    loadContext(supplierId)
  }

  const handleSubmit = async () => {
    if (!preselectedSupplier && !form.targetSupplierId) {
      setSupplierError(true)
      toast.error('Выберите партнёра')
      return
    }
    setSupplierError(false)

    const result = inviteFormSchema.safeParse({
      myB2bSegmentId: form.myB2bSegmentId ?? undefined,
    })
    if (!result.success) {
      const errs = result.error.flatten().fieldErrors
      setB2bSegmentError(!!errs.myB2bSegmentId)
      toast.error(errs.myB2bSegmentId?.[0] ?? 'Заполните обязательные поля')
      return
    }
    setB2bSegmentError(false)

    setSubmitting(true)
    const tId = toast.loading('Отправка приглашения...')
    try {
      const payload = {
        targetSupplierId: form.targetSupplierId,
        enableMySharing: form.enableMySharing,
        myEtaDays: form.myEtaDays,
        myIncomingEtaDays: form.myIncomingEtaDays,
        myOutgoingExclusions: form.localOutgoingExclusions,
        myIncomingExclusions: form.localIncomingExclusions,
        mySharedSegments: form.mySharedSegments,
        myB2bSegmentId: form.myB2bSegmentId,
      }
      const res = await api.post<{ partnership: PartnershipModel }>(
        '/partnerships/invite',
        payload
      )
      toast.success('Приглашение отправлено', { id: tId })
      onSuccess(res.data.partnership)
    } catch (e) {
      toast.error('Ошибка при отправке', { id: tId })
      handleApiError(e, modal)
    } finally {
      setSubmitting(false)
    }
  }

  const showForm = !!contextData

  const title =
    titleOverride ??
    (preselectedSupplier
      ? `Запросить партнёрство — ${preselectedSupplier.description}`
      : 'Пригласить партнёра')

  return (
    <Modal
      title={title}
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText="Отправить приглашение"
      cancelText="Отмена"
      confirmLoading={submitting}
      okButtonProps={{ disabled: !showForm }}
      centered
      width={580}
      destroyOnHidden
      wrapClassName={themeClass}
    >
      {/* Initial directory loading — full loader */}
      {directoryLoading ? (
        <div className={cn.loaderWrap}>
          <Loader size={24} />
        </div>
      ) : (
        <div className={cn.form}>
          {/* Partner: readonly banner or select */}
          {preselectedSupplier ? (
            <div className={cn.preselectedBanner}>
              <span className={cn.bannerLabel}>Партнёр</span>
              <span className={cn.bannerValue}>
                {preselectedSupplier.description}
              </span>
              {preselectedSupplier.legalAddress && (
                <span className={cn.bannerSub}>
                  {preselectedSupplier.legalAddress}
                </span>
              )}
            </div>
          ) : (
            <div className={cn.field}>
              <label className={cn.label}>
                Партнёр <span className={cn.required}>*</span>
              </label>
              {directoryItems.length === 0 ? (
                <div className={cn.emptyHint}>
                  Нет доступных поставщиков для приглашения
                </div>
              ) : (
                <>
                  <Select
                    popupClassName={themeClass}
                    value={form.targetSupplierId || undefined}
                    onChange={handleSupplierSelect}
                    placeholder="Выберите партнёра"
                    className={cn.fullWidth}
                    status={supplierError ? 'error' : undefined}
                    options={directoryItems.map((item) => ({
                      value: item.supplier.id,
                      label: item.supplier.description,
                    }))}
                  />
                  {supplierError && (
                    <span className={cn.errorText}>Выберите партнёра</span>
                  )}
                </>
              )}
            </div>
          )}

          {/* Context loading spinner — shown below supplier, doesn't hide it */}
          {contextLoading && (
            <div className={cn.loaderWrap}>
              <Loader size={20} />
            </div>
          )}

          {showForm && (
            <>
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                  { key: 'sell', label: 'Продаю' },
                  { key: 'buy', label: 'Покупаю' },
                ]}
              />

              {activeTab === 'sell' && (
                <div className={cn.roleSection}>
                  <div className={cn.section}>
                    <div className={cn.sectionTitle}>
                      Исходящая логистика (Что мы отдаём партнёру)
                    </div>
                    <div className={cn.switchRow}>
                      <Switch
                        checked={form.enableMySharing}
                        onChange={(v) =>
                          setForm((prev) => ({ ...prev, enableMySharing: v }))
                        }
                        size="small"
                      />
                      <span className={cn.switchLabel}>
                        Транслировать мои остатки партнёру
                      </span>
                    </div>

                    {form.enableMySharing && (
                      <>
                        <Checkbox
                          checked={form.shareAllOutgoing}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setForm((prev) => ({
                                ...prev,
                                shareAllOutgoing: true,
                                localOutgoingExclusions: [],
                              }))
                            } else {
                              setForm((prev) => ({
                                ...prev,
                                shareAllOutgoing: false,
                              }))
                            }
                          }}
                        >
                          Транслировать все доступные склады
                        </Checkbox>

                        {!form.shareAllOutgoing && (
                          <div className={cn.storeList}>
                            <div className={cn.storeHint}>
                              Снимите галочку — склад будет{' '}
                              <strong>скрыт</strong> от партнёра
                            </div>

                            {contextData.outgoing.direct && (
                              <div className={cn.supplierStoreGroup}>
                                <div className={cn.supplierGroupLabel}>
                                  Собственные склады
                                </div>
                                <div className={cn.groupStores}>
                                  {contextData.outgoing.direct.stores.map(
                                    (store) => (
                                      <div
                                        key={store.code}
                                        className={cn.storeItem}
                                      >
                                        <Checkbox
                                          checked={
                                            !form.localOutgoingExclusions.includes(
                                              store.code
                                            )
                                          }
                                          onChange={(e) => {
                                            const excluded = !e.target.checked
                                            setForm((prev) => ({
                                              ...prev,
                                              localOutgoingExclusions: excluded
                                                ? [
                                                    ...prev.localOutgoingExclusions,
                                                    store.code,
                                                  ]
                                                : prev.localOutgoingExclusions.filter(
                                                    (c) => c !== store.code
                                                  ),
                                            }))
                                          }}
                                        >
                                          <StoreLabel store={store} />
                                        </Checkbox>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}

                            {contextData.outgoing.transit.map(
                              (transitGroup, idx) => (
                                <div
                                  key={idx}
                                  className={cn.supplierStoreGroup}
                                >
                                  <div className={cn.supplierGroupLabel}>
                                    {transitGroup.transitRoute
                                      .map((r) => r.description)
                                      .join(' → ')}
                                  </div>
                                  <div className={cn.groupStores}>
                                    {transitGroup.stores.map((store) => (
                                      <div
                                        key={store.code}
                                        className={cn.storeItem}
                                      >
                                        <Checkbox
                                          checked={
                                            !form.localOutgoingExclusions.includes(
                                              store.code
                                            )
                                          }
                                          onChange={(e) => {
                                            const excluded = !e.target.checked
                                            setForm((prev) => ({
                                              ...prev,
                                              localOutgoingExclusions: excluded
                                                ? [
                                                    ...prev.localOutgoingExclusions,
                                                    store.code,
                                                  ]
                                                : prev.localOutgoingExclusions.filter(
                                                    (c) => c !== store.code
                                                  ),
                                            }))
                                          }}
                                        >
                                          <StoreLabel store={store} />
                                        </Checkbox>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )
                            )}

                            {!contextData.outgoing.direct &&
                              contextData.outgoing.transit.length === 0 && (
                                <div className={cn.emptyHint}>
                                  Нет доступных складов
                                </div>
                              )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'buy' &&
                (contextData.incoming.direct !== null ||
                  contextData.incoming.transit.length > 0) && (
                  <div className={cn.roleSection}>
                    <div className={cn.section}>
                      <div className={cn.sectionTitle}>
                        Входящая логистика (Что мы принимаем от партнёра)
                      </div>
                      <Checkbox
                        checked={form.acceptAllIncoming}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setForm((prev) => ({
                              ...prev,
                              acceptAllIncoming: true,
                              localIncomingExclusions: [],
                            }))
                          } else {
                            setForm((prev) => ({
                              ...prev,
                              acceptAllIncoming: false,
                            }))
                          }
                        }}
                      >
                        Принимать все транслируемые склады
                      </Checkbox>

                      {!form.acceptAllIncoming && (
                        <div className={cn.storeList}>
                          <div className={cn.storeHint}>
                            Снимите галочку — остатки этого склада{' '}
                            <strong>не будут</strong> отображаться у вас
                          </div>

                          {contextData.incoming.direct && (
                            <div className={cn.supplierStoreGroup}>
                              <div className={cn.supplierGroupLabel}>
                                Собственные склады партнёра
                              </div>
                              <div className={cn.groupStores}>
                                {contextData.incoming.direct.stores.map(
                                  (store) => (
                                    <div
                                      key={store.code}
                                      className={cn.storeItem}
                                    >
                                      <Checkbox
                                        checked={
                                          !form.localIncomingExclusions.includes(
                                            store.code
                                          )
                                        }
                                        onChange={(e) => {
                                          const excluded = !e.target.checked
                                          setForm((prev) => ({
                                            ...prev,
                                            localIncomingExclusions: excluded
                                              ? [
                                                  ...prev.localIncomingExclusions,
                                                  store.code,
                                                ]
                                              : prev.localIncomingExclusions.filter(
                                                  (c) => c !== store.code
                                                ),
                                          }))
                                        }}
                                      >
                                        <StoreLabel store={store} />
                                      </Checkbox>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                          {contextData.incoming.transit.map(
                            (transitGroup, idx) => (
                              <div key={idx} className={cn.supplierStoreGroup}>
                                <div className={cn.supplierGroupLabel}>
                                  {transitGroup.transitRoute
                                    .map((r) => r.description)
                                    .join(' → ')}
                                </div>
                                <div className={cn.groupStores}>
                                  {transitGroup.stores.map((store) => (
                                    <div
                                      key={store.code}
                                      className={cn.storeItem}
                                    >
                                      <Checkbox
                                        checked={
                                          !form.localIncomingExclusions.includes(
                                            store.code
                                          )
                                        }
                                        onChange={(e) => {
                                          const excluded = !e.target.checked
                                          setForm((prev) => ({
                                            ...prev,
                                            localIncomingExclusions: excluded
                                              ? [
                                                  ...prev.localIncomingExclusions,
                                                  store.code,
                                                ]
                                              : prev.localIncomingExclusions.filter(
                                                  (c) => c !== store.code
                                                ),
                                          }))
                                        }}
                                      >
                                        <StoreLabel store={store} />
                                      </Checkbox>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          )}

                          {!contextData.incoming.direct &&
                            contextData.incoming.transit.length === 0 && (
                              <div className={cn.emptyHint}>
                                Нет доступных складов
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

              {activeTab === 'buy' &&
                contextData.incoming.direct === null &&
                contextData.incoming.transit.length === 0 && (
                  <div className={cn.roleSection}>
                    <div className={cn.emptyHint}>
                      Партнёр не транслирует доступные склады
                    </div>
                  </div>
                )}

              {activeTab === 'buy' && (
                <div className={cn.roleSection}>
                  <div className={cn.field}>
                    <label className={cn.label}>
                      Дополнительное время на приемку (дней)
                    </label>
                    <Input
                      type="number"
                      min={0}
                      value={form.myIncomingEtaDays}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          myIncomingEtaDays: Math.max(
                            0,
                            parseInt(e.target.value) || 0
                          ),
                        }))
                      }
                      className={cn.etaInput}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'sell' && (
                <div className={cn.roleSection}>
                  <div className={cn.sectionTitle}>
                    Условия поставки партнёру
                  </div>
                  <div className={cn.field}>
                    <label className={cn.label}>
                      Доставка до партнёра (дней)
                    </label>
                    <Input
                      type="number"
                      min={0}
                      value={form.myEtaDays}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          myEtaDays: parseInt(e.target.value) || 0,
                        }))
                      }
                      className={cn.etaInput}
                    />
                  </div>

                  <div className={cn.field}>
                    <label className={cn.label}>
                      Сегменты для расширения (как База)
                    </label>
                    <div className={cn.fieldHint}>
                      Партнёр сможет создавать свои сегменты с наценкой на
                      основе этих
                    </div>
                    <Select
                      mode="multiple"
                      popupClassName={themeClass}
                      value={form.mySharedSegments}
                      onChange={(v) =>
                        setForm((prev) => ({ ...prev, mySharedSegments: v }))
                      }
                      placeholder="Выберите сегменты"
                      className={cn.fullWidth}
                      options={contextData.myAvailableSegments.map((s) => ({
                        value: s.id,
                        label: s.description,
                      }))}
                    />
                  </div>

                  <div className={cn.field}>
                    <label className={cn.label}>
                      Сегмент для взаиморасчётов (B2B прайс){' '}
                      <span className={cn.required}>*</span>
                    </label>
                    <div className={cn.fieldHint}>
                      По этому сегменту партнёр будет покупать у вас напрямую
                    </div>
                    <Select
                      popupClassName={themeClass}
                      value={form.myB2bSegmentId || undefined}
                      onChange={(v) => {
                        setB2bSegmentError(false)
                        setForm((prev) => ({
                          ...prev,
                          myB2bSegmentId: v ?? null,
                        }))
                      }}
                      placeholder="Выберите сегмент"
                      allowClear
                      className={cn.fullWidth}
                      status={b2bSegmentError ? 'error' : undefined}
                      options={contextData.myAvailableSegments.map((s) => ({
                        value: s.id,
                        label: s.description,
                      }))}
                    />
                    {b2bSegmentError && (
                      <span className={cn.errorText}>Выберите B2B сегмент</span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  )
}

export default InviteModal
